/* eslint-disable @typescript-eslint/naming-convention */

import {dayjs} from '@karve.it/core';

import { MAX_32_BIT_INT } from './global.constants';

export function currentTimeSeconds() {
	// eslint-disable-next-line @typescript-eslint/no-magic-numbers
	return Math.round(Date.now() / 1000);
}

export const MS_ONE_SECOND = 1000;
export const MS_TEN_SECONDS = 10000;
export const MS_ONE_MINUTE = 60000;
export const MS_TEN_MINUTES = 600000;
export const MS_ONE_HOUR = 3600000;
export const MS_ONE_DAY = 86400000;
export const MS_ONE_WEEK = 604800000;
export const MS_ONE_MONTH = 2592000000;
export const MS_ONE_YEAR = 31536000000;

export const S_ONE_MINUTE = 60;
export const S_ONE_HOUR = 3600;
export const S_THREE_HOURS = 10800;
export const S_ONE_DAY = 86400;
export const S_ONE_WEEK = 604800;
export const S_FOUR_WEEKS = 2419200;
export const S_ONE_YEAR = 31536000;
export const S_TWO_YEARS = 63072000;
export const S_TEN_YEARS = 3.154e+8;

export const HOURS_TO_SECONDS = 3600;
// eslint-disable-next-line @typescript-eslint/no-magic-numbers
export const UNIX_MS_S_THRESHOLD = currentTimeSeconds() * 10;

export type DateFormat = 'mm/dd/yy' | 'dd/mm/yy';
export type TimeFormat = 12 | 24;
export const dateTimeOnlyFormat = 'DD/MM/YYYY HH:mm';


// eslint-disable-next-line no-shadow
export enum MONTH_INDEX {
	JANUARY,
	FEBRURARY,
	MARCH,
	APRIL,
	MAY,
	JUNE,
	JULY,
	AUGUST,
	SEPTEMBER,
	OCTOBER,
	NOVEMBER,
	DECEMBER,
}

/**
 * Divides val by 1000 if val is greater than UNIX_MS_S_THRESHOLD
 *
 * @param val unix seconds or milliseconds
 * @returns integer
 */
export function ensureUnixSeconds(val?: number) {
	if (val === undefined || val === null) { return val; }
	if (val > UNIX_MS_S_THRESHOLD) {
		return Math.round(val / MS_ONE_SECOND);
	}
	return val;
}

export async function resolveMilliseconds(milliseconds: number) {
	return new Promise(async (resolve) => {
		setTimeout(resolve, milliseconds);
	});
}

export async function resolveNever() {
	return new Promise(async () => {});
}

export function timeInSpan(time: number, start: number, end: number) {
	return time >= start && time <= end;
}

export function dateToDateString(date: Date) {
	return `${ date.getFullYear() }/${ date.getMonth() + 1 }/${ date.getDate() }`;
}

export function getCurrentDateStringFromDayJs(){
	return dayjs().format('YYYY-MM-DD') + 'T00:00';
}

/**
 * Convert a date from the american format to a useable format for javascript
 *
 * @param date Date string in format 'MM/DD/YYYY'
 * @param includeTime If you want to initialize a date object this must be true otherwise the date is initalized in GMT
 * @returns
 */
export function convertCalendarFormatToStandard(date: string | Date, includeTime = false): string{

	if (date instanceof Date) {
		return dayjs(date).format(includeTime ? `YYYY-MM-DDTHH:mm` : `YYYY-MM-DD`);
	}

	const [month, day, year] = date?.split('/');
    const formattedDate = `${year}-${month}-${day}${includeTime ? 'T00:00' : ''}`;

	return formattedDate;
}

/**
 * Returns an array of numbers in the format
 * [year, month, day, hour, minute, second, millisecond]
 * which can be accepted by moment to parse a date without conflicting with
 * the moment default timezone
 *
 * @param date date object
 * @returns tuple of numbers
 */
export function dateToParts(date: Date): [ number, number, number, number, number, number, number ] {
	return [
		date.getFullYear(),
		date.getMonth(),
		date.getDate(),
		date.getHours(),
		date.getMinutes(),
		date.getSeconds(),
		date.getMilliseconds(),
	];
}

export function dateToUnixSeconds(date: Date) {
	return Math.floor(date.getTime() / 1000);
}

export function getBeginningOfTimeUnix() {
	return dayjs().subtract(30, 'years').unix();
}

export function getEndOfTimeUnix() {
	return Math.min(dayjs().endOf('day').unix(), MAX_32_BIT_INT);;
}


/**
 * Converts a timestamp in seconds to a string in the format MM/DD/YYYY considering timezone
 * @param timestampInSeconds The timestamp in seconds
 * @param tz The desired timezone (default is 'UTC')
 * @returns Formatted date string in MM/DD/YYYY
 */
export function timestampToDateString(timestampInSeconds: number, tz: string = 'UTC'): string {
    return dayjs.unix(timestampInSeconds).tz(tz).format('MM/DD/YYYY');
}

/**
 * Normalizes a date string from legacy format (MM-DD-YYYY) to canonical format (YYYY-MM-DD).
 * If the string is already in canonical format, returns it as-is.
 * If the string is in neither format, returns undefined.
 *
 * @param dateStr A date string in either 'MM-DD-YYYY' or 'YYYY-MM-DD' format
 * @returns The date string in 'YYYY-MM-DD' format, or undefined if invalid
 */
export function normalizeDateParamString(dateStr: string | undefined): string | undefined {
    if (!dateStr) {
        return undefined;
    }

    const parts = dateStr.split('-');
    if (parts.length !== 3) {
        return undefined;
    }

    const [first, second, third] = parts;

    // Canonical format: YYYY-MM-DD (first segment is 4 digits)
    if (first.length === 4 && second.length === 2 && third.length === 2) {
        return dateStr;
    }

    // Legacy format: MM-DD-YYYY (first segment is 2 digits, last is 4 digits)
    if (first.length === 2 && second.length === 2 && third.length === 4) {
        return `${third}-${first}-${second}`;
    }

    return undefined;
}

/**
 * Normalizes a date range query param string (format: "date1;date2") from legacy to canonical format.
 * Each date in the range is normalized independently.
 *
 * @param dateRangeParam A date range string in format "date1;date2"
 * @returns The normalized date range string, or undefined if empty/invalid
 */
export function normalizeDateRangeParam(dateRangeParam: string | undefined): string | undefined {
    if (!dateRangeParam) {
        return undefined;
    }

    const dates = dateRangeParam.split(';');
    const normalizedDates = dates
        .map(normalizeDateParamString)
        .filter((d): d is string => d !== undefined);

    if (normalizedDates.length === 0) {
        return undefined;
    }

    return normalizedDates.join(';');
}
